import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePrevisionData } from '../hooks/usePrevisionData';
import { useAllModelsStatus } from '../hooks/useAllModelsStatus';
import PrevisionView from '../components/PrevisionView';
import ArticleSelector from '../components/ArticleSelector';
import { Article } from '../types/api.types';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import {
  calculateAverageModelQuality,
  MODEL_QUALITY_AVERAGE_LABEL,
} from '../utils/modelQuality';

// Get display state for a model based on status and prediction availability
// modelData: from previsionData.modelPredictions (may be undefined if not fetched yet)
// modelInfo: from availableModels API response (always available)
const getModelDisplayState = (modelData: any, modelInfo?: any) => {
  // Priority: modelData.status > modelInfo.status > 'not_trained'
  // modelInfo comes from available-models API which queries computation server (source of truth)
  const status = modelData?.status || modelInfo?.status || 'not_trained';
  const hasError = !!modelData?.error;
  const hasData = modelData?.data && modelData.data.length > 0;
  // Use has_predictions from API (backend database) or modelData
  const hasPredictions = modelData?.hasPredictions ?? modelInfo?.has_predictions ?? false;
  
  // Determine the actual state
  if (hasError && status === 'error') {
    return {
      type: 'error',
      message: 'Erreur',
      description: modelData.error || modelInfo?.error_message || 'Une erreur est survenue',
      color: 'red',
      badge: 'Erreur'
    };
  }
  
  if (status === 'not_trained' || status === 'not_available') {
    return {
      type: 'not_trained',
      message: 'Non entraîné',
      description: 'Le modèle n\'a pas encore été entraîné',
      color: 'gray',
      badge: 'Non entraîné'
    };
  }
  
  if (status === 'training') {
    return {
      type: 'training',
      message: 'Entraînement en cours',
      description: 'Le modèle est en cours d\'entraînement',
      color: 'blue',
      badge: 'Entraînement...',
      animated: true
    };
  }
  
  if (status === 'predicting') {
    return {
      type: 'predicting',
      message: 'Génération en cours',
      description: 'Les prédictions sont en cours de génération',
      color: 'blue',
      badge: 'Génération...',
      animated: true
    };
  }
  
  if (status === 'available' && !hasData && !hasPredictions) {
    return {
      type: 'no_predictions',
      message: 'Aucune prédiction',
      description: 'Le modèle est entraîné mais aucune prédiction n\'est disponible',
      color: 'yellow',
      badge: 'Aucune prédiction'
    };
  }
  
  if (status === 'available' && hasData) {
    return {
      type: 'available',
      message: 'Disponible',
      description: '',
      color: 'green',
      badge: 'Disponible' // Show badge so status is always visible
    };
  }
  
  // If status is 'available' but no data yet, show as available (predictions might be loading)
  if (status === 'available') {
    return {
      type: 'available',
      message: 'Disponible',
      description: '',
      color: 'green',
      badge: 'Disponible' // Show badge so status is always visible
    };
  }
  
  // Default fallback - should rarely happen now
  return {
    type: 'unknown',
    message: 'État inconnu',
    description: `Status: ${status}`,
    color: 'gray',
    badge: 'Inconnu'
  };
};

// Find the model with highest average confidence
const findBestModel = (modelPredictions: any[]): string | null => {
  if (!modelPredictions || modelPredictions.length === 0) return null;
  
  let bestModel: string | null = null;
  let highestConfidence = -1;
  
  modelPredictions.forEach(model => {
    // Only consider models that:
    // 1. Have no error (or error is not a real error, just missing predictions)
    // 2. Have data available
    // 3. Are in 'available' status (trained and ready)
    const displayState = getModelDisplayState(model);
    const hasData = model.data && model.data.length > 0;
    const isAvailable = displayState.type === 'available';
    
    if (isAvailable && hasData) {
      const avgQuality = calculateAverageModelQuality(model.data);
      if (avgQuality === null) {
        return;
      }
      if (avgQuality > highestConfidence) {
        highestConfidence = avgQuality;
        bestModel = model.id;
      }
    }
  });
  
  return bestModel;
};

const Previsions: React.FC = () => {
  const { activeDatabase } = useDatabaseContext();
  const { 
    previsionData, 
    availableModels,
    applyFilters, 
    refetchModelData,
    refetchAvailableModels,
    filters,
  } = usePrevisionData(activeDatabase?.id || null);

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedArticles, setSelectedArticles] = useState<Article[]>([]); // Track actual selected articles
  const [showRegeneratePopup, setShowRegeneratePopup] = useState<boolean>(false);
  const [showTrainingPopup, setShowTrainingPopup] = useState<boolean>(false);
  const [trainingJustStarted, setTrainingJustStarted] = useState<boolean>(false);
  const [predictionJustStarted, setPredictionJustStarted] = useState<boolean>(false);
  const startedTaskIdRef = useRef<string | null>(null);

  // Bulk status for all models (polls when training/prediction in progress - fixes refresh bug)
  const {
    models: modelsStatus,
    refetch: refetchStatus,
    getEffectiveStatus,
  } = useAllModelsStatus(
    activeDatabase?.id || null,
    3000,
    trainingJustStarted || predictionJustStarted
  );

  const modelStatus = selectedModel ? getEffectiveStatus(selectedModel) : null;
  const trainingTaskId = selectedModel ? modelsStatus[selectedModel]?.training_task_id ?? null : null;

  // Track when we've seen 'training' (so we can distinguish "available before task started" from "available after training done")
  React.useEffect(() => {
    if (modelStatus === 'training') hasSeenTrainingRef.current = true;
  }, [modelStatus]);
  // Clear spinner when training is done: (1) error, or (2) available + we've seen 'training', or
  // (3) available + training_task_id matches our started task
  React.useEffect(() => {
    if (modelStatus === 'error') {
      setTrainingJustStarted(false);
      hasSeenTrainingRef.current = false;
      startedTaskIdRef.current = null;
    } else if (modelStatus === 'available') {
      const ourTask = startedTaskIdRef.current;
      const taskCompleted = ourTask && trainingTaskId === ourTask;
      if (hasSeenTrainingRef.current || taskCompleted) {
        setTrainingJustStarted(false);
        hasSeenTrainingRef.current = false;
        startedTaskIdRef.current = null;
      }
    }
  }, [modelStatus, trainingTaskId]);
  // Clear prediction spinner when no longer predicting
  React.useEffect(() => {
    if (modelStatus !== 'predicting') setPredictionJustStarted(false);
  }, [modelStatus]);

  // Refetch when spinner stops (training or prediction completed) - view updates automatically
  const prevModelStatusRef = useRef<string | null>(null);
  React.useEffect(() => {
    const prev = prevModelStatusRef.current;
    const wasActive = prev === 'training' || prev === 'predicting';
    const isTerminal = modelStatus === 'available' || modelStatus === 'error';
    if (wasActive && isTerminal && selectedModel) {
      const wasPredicting = prev === 'predicting';
      prevModelStatusRef.current = modelStatus;
      const timeoutId = setTimeout(() => {
        refetchStatus();
        refetchAvailableModels?.();
        if (wasPredicting) {
          refetchModelData(selectedModel);
          setShowModelModal(false); // Close modal when prediction completes
        }
      }, 2500);
      return () => clearTimeout(timeoutId);
    }
    prevModelStatusRef.current = modelStatus;
  }, [modelStatus, selectedModel, refetchStatus, refetchAvailableModels, refetchModelData]);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [disabledModels, setDisabledModels] = useState<Set<string>>(new Set()); // Track disabled models
  const [colorSeed] = useState<number>(75);
  const [showModelModal, setShowModelModal] = useState<boolean>(false);
  const initializedRef = useRef<string>(''); // Track which data we've initialized for
  const lastDatabaseIdRef = useRef<number | string | null>(null); // Track database changes
  const hasAutoSelectedRef = useRef<boolean>(false); // Track if we've done initial auto-selection for this database
  const hasSeenTrainingRef = useRef<boolean>(false); // True once we've seen status 'training' (so we know training actually ran)
  
  // Reset model selection when database changes
  React.useEffect(() => {
    if (activeDatabase?.id !== lastDatabaseIdRef.current) {
      // Database changed, reset state
      setSelectedModel(null);
      setDisabledModels(new Set());
      initializedRef.current = '';
      hasAutoSelectedRef.current = false; // Reset auto-selection flag for new database
      lastDatabaseIdRef.current = activeDatabase?.id || null;
    }
  }, [activeDatabase?.id]);

  // Reset model selection when filters change - force best model when new data arrives
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => {
    hasAutoSelectedRef.current = false;
    initializedRef.current = '';
  }, [filtersKey]);
  
  const handleArticlesChange = (arts: Article[]) => {
    // Update the selected articles state
    setSelectedArticles(arts);
  };

  // Determine the selected article (if only one is selected)
  const selectedArticle = selectedArticles.length === 1 ? selectedArticles[0] : undefined;

  // Find the best model
  const bestModelId = findBestModel(previsionData.modelPredictions);

  const modelsToFetch = useMemo(
    () => availableModels.filter((m: any) => m.has_predictions === true),
    [availableModels]
  );
  const modelsStillLoading = useMemo(
    () => previsionData.modelPredictions.filter(
      (m) => modelsToFetch.some((p: any) => p.id === m.id) && m.isLoading
    ),
    [previsionData.modelPredictions, modelsToFetch]
  );
  const allModelsReceived =
    modelsToFetch.length > 0 &&
    modelsToFetch.every((model: any) => previsionData.modelPredictions.some((prediction) => prediction.id === model.id)) &&
    modelsStillLoading.length === 0;
  const selectionKey = `${activeDatabase?.id || 'none'}-${filtersKey}`;
  const hasCompletedAutoSelection =
    hasAutoSelectedRef.current &&
    initializedRef.current.startsWith(`${selectionKey}-`);
  const isBestModelSelectionPending =
    Boolean(activeDatabase?.id) &&
    availableModels.length > 0 &&
    modelsToFetch.length > 0 &&
    !hasCompletedAutoSelection &&
    (!allModelsReceived || Boolean(bestModelId));
  const displayedSelectedModel = isBestModelSelectionPending ? null : selectedModel;
  const displayedDisabledModels = useMemo(
    () => isBestModelSelectionPending
      ? new Set(availableModels.map((model: any) => model.id))
      : disabledModels,
    [isBestModelSelectionPending, availableModels, disabledModels]
  );

  // Initialize disabled models: disable all except the best model by default
  React.useEffect(() => {
    // Don't run if we don't have a database
    if (!activeDatabase?.id) return;

    const bestModel = findBestModel(previsionData.modelPredictions);

    // Check if best model has data available (not just that it exists)
    const bestModelData = bestModel ? previsionData.modelPredictions.find((m) => m.id === bestModel) : null;
    const hasBestModelData =
      bestModelData && !bestModelData.error && bestModelData.data && bestModelData.data.length > 0;

    // Create a key to track if we've processed this data
    const dataKey = `${selectionKey}-${previsionData.modelPredictions.length}-${bestModel || 'none'}-${hasBestModelData ? 'hasData' : 'noData'}`;

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Previsions] best-model-selection', {
        bestModel,
        hasBestModelData,
        allModelsReceived,
        modelsToFetchCount: modelsToFetch.length,
        modelsStillLoadingCount: modelsStillLoading.length,
        hasAutoSelectedRef: hasAutoSelectedRef.current,
        initializedRef: initializedRef.current,
        dataKey,
      });
    }

    // Only proceed if:
    // 1. We have available models
    // 2. We have a best model with actual data
    // 3. We haven't initialized for this exact state yet
    if (availableModels.length > 0 && bestModel && hasBestModelData && allModelsReceived && initializedRef.current !== dataKey) {
      // Only disable models and auto-select on INITIAL load (when hasAutoSelectedRef is false)
      // CRITICAL: Only lock selection (hasAutoSelectedRef=true) when ALL models have been fetched.
      // Otherwise we may select the first model that arrives, not the actual best.
      if (!hasAutoSelectedRef.current) {
        const allModelIds = new Set(availableModels.map((m: any) => m.id));
        const newDisabledModels = new Set(allModelIds);
        newDisabledModels.delete(bestModel);
        setDisabledModels(newDisabledModels);
        setSelectedModel(bestModel);

        hasAutoSelectedRef.current = true;
      }
      initializedRef.current = dataKey;
    }
  }, [previsionData.modelPredictions, availableModels, activeDatabase?.id, allModelsReceived, modelsToFetch, modelsStillLoading, selectionKey]);

  // Handle model state changes
  const handleModelClick = (modelId: string) => {
    if (selectedModel === modelId) {
      // If clicking the selected model, disable it
      setDisabledModels(prev => {
        const newSet = new Set(prev);
        newSet.add(modelId);
        return newSet;
      });
      setSelectedModel(null); // Clear selection
    } else if (disabledModels.has(modelId)) {
      // If clicking a disabled model, enable it and select it
      setDisabledModels(prev => {
        const newSet = new Set(prev);
        newSet.delete(modelId);
        return newSet;
      });
      setSelectedModel(modelId);
    } else {
      // If clicking an enabled model, select it
      setSelectedModel(modelId);
    }
  };

  // Handle training
  const handleTrainModel = async () => {
    if (!activeDatabase || !selectedModel || disabledModels.has(selectedModel)) {
      alert('Veuillez sélectionner une base de données et un modèle valide');
      return;
    }

    setIsRegenerating(true);
    try {
      const response = await api.post(
        `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.RUN_PREDICTION(activeDatabase.id)}`,
        { model_name: selectedModel, retrain: true }
      );
      
      // Check if training was triggered vs already in progress
      if (response.data.status === 'training_started') {
        setShowTrainingPopup(true);
        setTrainingJustStarted(true);  // Show spinner immediately, keep modal open
        hasSeenTrainingRef.current = false;  // Reset for this training session
        startedTaskIdRef.current = response.data.task_id || null;  // When status returns this as last_completed, we're done
        refetchStatus();
      } else if (response.data.status === 'training_in_progress') {
        setTrainingJustStarted(true);
        hasSeenTrainingRef.current = true;  // Already training
        startedTaskIdRef.current = response.data.task_id || null;
        alert('Le modèle est déjà en cours d\'entraînement');
      }
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Échec du lancement de l\'entraînement. Veuillez réessayer.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle prediction generation
  const handleGeneratePredictions = async () => {
    if (!activeDatabase || !selectedModel || disabledModels.has(selectedModel)) {
      alert('Veuillez sélectionner une base de données et un modèle valide');
      return;
    }

    setIsRegenerating(true);
    try {
      const response = await api.post(
        `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.RUN_PREDICTION(activeDatabase.id)}`,
        { model_name: selectedModel }
      );
      
      // Check if training was triggered (shouldn't happen if model is available)
      if (response.data.model_status === 'training') {
        setShowTrainingPopup(true);
        setTrainingJustStarted(true);
      } else if (response.data.status === 'prediction_started') {
        setPredictionJustStarted(true);  // Path A: modal stays open, spinner on button
        // No regeneration popup - spinner in modal is the main feedback
      }
      
      refetchStatus();
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.response?.data?.error || error.message || 'Échec de la génération des prévisions. Veuillez réessayer.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle model data refetch for corrections
  const handleModelDataRefetch = useCallback(() => {
    if (selectedModel && refetchModelData) {
      refetchModelData(selectedModel);
    }
  }, [selectedModel, refetchModelData]);

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-start">
        <div className="flex-1">
          <ArticleSelector
            onApplyFilters={applyFilters}
            onSelectionChange={() => {}}
            onArticlesChange={handleArticlesChange}
          />
        </div>
        
        {/* Model Selector Button */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowModelModal(true)}
            className="px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {isBestModelSelectionPending
                ? 'Sélection du meilleur modèle...'
                : selectedModel 
                ? (() => {
                    const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
                    const modelName = selectedModelInfo?.name || 'Modèle sélectionné';
                    const modelData = previsionData.modelPredictions.find(m => m.id === selectedModel);
                    const avgQuality = modelData && !modelData.error && modelData.data
                      ? calculateAverageModelQuality(modelData.data)
                      : null;
                    const nameWithQuality = avgQuality !== null
                      ? `${modelName} (${avgQuality.toFixed(1)}%)`
                      : modelName;
                    return nameWithQuality;
                  })()
                : 'Sélectionner un modèle'}
            </span>
            {!isBestModelSelectionPending && selectedModel && (() => {
              const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
              const modelData = previsionData.modelPredictions.find(m => m.id === selectedModel);
              const liveStatus = getEffectiveStatus(selectedModel);
              const modelInfo = liveStatus != null ? { ...selectedModelInfo, status: liveStatus } : selectedModelInfo;
              const displayState = getModelDisplayState(modelData, modelInfo);
              return (
                <>
                  {displayState.badge && (
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      displayState.color === 'red' ? 'bg-red-100 text-red-700' :
                      displayState.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                      displayState.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    } ${displayState.animated ? 'animate-pulse' : ''}`}>
                      {displayState.badge}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    disabledModels.has(selectedModel) 
                      ? 'bg-gray-100 text-gray-500' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {disabledModels.has(selectedModel) ? 'Désactivé' : 'Actif'}
                  </span>
                </>
              );
            })()}
          </button>
        </div>
      </div>

      {/* Model Selection Modal */}
      {showModelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModelModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-4xl mx-4 max-h-[90vh] overflow-y-auto shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Sélection du modèle de prédiction</h2>
              <button
                onClick={() => setShowModelModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Model Selection Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {availableModels.map(model => {
                  const modelData = previsionData.modelPredictions.find(m => m.id === model.id);
                  const isSelected = displayedSelectedModel === model.id;
                  const isDisabled = displayedDisabledModels.has(model.id);
                  const isBest = bestModelId === model.id;
                  // Use live status from bulk polling for all models
                  const liveStatus = getEffectiveStatus(model.id);
                  const modelInfo = liveStatus != null
                    ? { ...model, status: liveStatus }
                    : model;
                  const displayState = getModelDisplayState(modelData, modelInfo);
                  const avgQuality = modelData && displayState.type === 'available'
                    ? calculateAverageModelQuality(modelData.data || [])
                    : null;
                  
                  // Determine button styling based on state
                  let buttonClasses = 'p-4 rounded-lg text-sm font-medium transition-colors flex flex-col items-start border-2 text-left';
                  
                  if (isDisabled) {
                    buttonClasses += ' bg-gray-50 text-gray-400 border-gray-200 cursor-pointer';
                  } else if (isSelected) {
                    buttonClasses += ' bg-blue-100 text-blue-700 border-blue-300';
                  } else {
                    // Apply color based on display state
                    switch (displayState.color) {
                      case 'red':
                        buttonClasses += ' bg-red-50 text-red-700 hover:bg-red-100 border-red-200';
                        break;
                      case 'yellow':
                        buttonClasses += ' bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200';
                        break;
                      case 'blue':
                        buttonClasses += ' bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200';
                        break;
                      case 'green':
                        buttonClasses += ' bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent';
                        break;
                      default:
                        buttonClasses += ' bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent';
                    }
                  }
                  
                  if (isBest && !isDisabled && displayState.type === 'available') {
                    buttonClasses += ' ring-2 ring-green-300';
                  }
                  
                  return (
                    <button
                      key={model.id}
                      onClick={() => handleModelClick(model.id)}
                      className={buttonClasses}
                    >
                      <div className="flex items-center justify-between w-full mb-2">
                        <span className="font-semibold">{model.name}</span>
                        <div className="flex gap-1 flex-wrap justify-end">
                          {isBest && !isDisabled && displayState.type === 'available' && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              Meilleur
                            </span>
                          )}
                          {displayState.badge && !isDisabled && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              displayState.color === 'red' ? 'bg-red-100 text-red-700' :
                              displayState.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                              displayState.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            } ${displayState.animated ? 'animate-pulse' : ''}`}>
                              {displayState.badge}
                            </span>
                          )}
                          {isDisabled && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                              Désactivé
                            </span>
                          )}
                        </div>
                      </div>
                      {displayState.description && !isDisabled && (
                        <div className="text-xs text-gray-500 mt-1">
                          {displayState.description}
                        </div>
                      )}
                      {displayState.type === 'available' && !isDisabled && avgQuality !== null && (
                        <div className="text-xs text-gray-500 mt-1">
                          {MODEL_QUALITY_AVERAGE_LABEL}: {avgQuality.toFixed(1)}%
                        </div>
                      )}
                      {isSelected && !isDisabled && (
                        <div className="text-xs text-blue-600 mt-1 font-medium">
                          ✓ Modèle sélectionné
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t">
                {/* Training Button (Left) - Always available unless currently training */}
                {displayedSelectedModel && (() => {
                  const baseModel = availableModels.find(m => m.id === displayedSelectedModel);
                  const selectedModelInfo = baseModel && modelStatus != null
                    ? { ...baseModel, status: modelStatus }
                    : baseModel;
                  const modelData = previsionData.modelPredictions.find(m => m.id === displayedSelectedModel);
                  const displayState = getModelDisplayState(modelData, selectedModelInfo);
                  // Spinner when we just started (trainingJustStarted) OR API says training. Keep spinning on
                  // first 'available' (race) until we've seen 'training' then 'available' (cleared via hasSeenTrainingRef).
                  const isTraining = trainingJustStarted || modelStatus === 'training';
                  const isError = displayState.type === 'error';
                  
                  return (
                    <button
                      onClick={() => {
                        handleTrainModel();
                        // Keep modal open so user sees training progress
                      }}
                      disabled={isRegenerating || isTraining || !activeDatabase || displayedDisabledModels.has(displayedSelectedModel)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isRegenerating || isTraining || displayedDisabledModels.has(displayedSelectedModel)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isError
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 border-2 border-orange-300'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-2 border-blue-300'
                      }`}
                    >
                      {isTraining ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Entraînement en cours
                        </span>
                      ) : isError ? (
                        '🔄 Réentraîner le modèle'
                      ) : (
                        '🎓 Entraîner le modèle'
                      )}
                    </button>
                  );
                })()}
                
                {/* Prediction Button (Right) - Path A: modal stays open, spinner like training */}
                {displayedSelectedModel && (() => {
                  const baseModel = availableModels.find(m => m.id === displayedSelectedModel);
                  const selectedModelInfo = baseModel && modelStatus != null
                    ? { ...baseModel, status: modelStatus }
                    : baseModel;
                  const modelData = previsionData.modelPredictions.find(m => m.id === displayedSelectedModel);
                  const displayState = getModelDisplayState(modelData, selectedModelInfo);
                  const canPredict = displayState.type === 'available' || displayState.type === 'no_predictions';
                  const isPredicting = predictionJustStarted || modelStatus === 'predicting';
                  
                  return (
                    <button
                      onClick={() => {
                        handleGeneratePredictions();
                        // Keep modal open so user sees prediction progress (Path A)
                      }}
                      disabled={isRegenerating || isPredicting || !activeDatabase || displayedDisabledModels.has(displayedSelectedModel) || !canPredict}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isRegenerating || isPredicting || !canPredict || displayedDisabledModels.has(displayedSelectedModel)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-green-100 text-green-700 hover:bg-green-200 border-2 border-green-300'
                      }`}
                    >
                      {isRegenerating ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Lancement...
                        </span>
                      ) : isPredicting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Génération en cours
                        </span>
                      ) : (
                        '🔄 Générer les prévisions'
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Training Popup */}
      {showTrainingPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(() => {
                  const selectedModelData = previsionData.modelPredictions.find(m => m.id === selectedModel);
                  const modelDisplayState = selectedModelData ? getModelDisplayState(selectedModelData) : null;
                  const isErrorState = modelStatus === 'error' || modelDisplayState?.type === 'error';
                  return isErrorState ? 'Réentraînement lancé' : 'Entraînement lancé';
                })()}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {(() => {
                  const selectedModelData = previsionData.modelPredictions.find(m => m.id === selectedModel);
                  const modelDisplayState = selectedModelData ? getModelDisplayState(selectedModelData) : null;
                  const isErrorState = modelStatus === 'error' || modelDisplayState?.type === 'error';
                  const action = isErrorState ? 'réentraînement' : 'entraînement';
                  return (
                    <>
                      Le {action} du modèle <strong>{selectedModel}</strong> a été lancé avec succès. Le processus peut prendre plusieurs heures et se déroule en arrière-plan.
                    </>
                  );
                })()}
              </p>
              <button
                onClick={() => setShowTrainingPopup(false)}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regeneration Success Popup */}
      {showRegeneratePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Régénération lancée
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                La régénération des prévisions pour le modèle <strong>{selectedModel}</strong> a été lancée avec succès. 
                Le processus peut prendre plusieurs heures et se déroule en arrière-plan.
              </p>
              <button
                onClick={() => setShowRegeneratePopup(false)}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}

      {previsionData.error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md">
          {previsionData.error}
        </div>
      )}

      {previsionData.isLoading && (
        <div className="bg-blue-50 text-blue-700 p-4 rounded-md">
          Loading historical data...
        </div>
      )}

      {isBestModelSelectionPending && (
        <div className="bg-blue-50 text-blue-700 p-4 rounded-md">
          Chargement des prévisions et sélection du meilleur modèle...
        </div>
      )}

      {/* Use the new PrevisionView component */}
      <PrevisionView 
        data={previsionData} 
        selectedArticle={selectedArticle}
        selectedModel={displayedSelectedModel}
        disabledModels={displayedDisabledModels}
        onModelDataRefetch={handleModelDataRefetch}
        colorSeed={colorSeed}
        filters={filters}
      />
    </div>
  );
};

export default Previsions;
